import React, { useState, useEffect, useRef } from "react";
import "./UserAssumption.less";
import service from "../../../../services/service";

interface UserAssumptionProps {
  isOpen: boolean;
  onClose: () => void;
  projectName: string;
  onValuationUpdate?: (valuation: number, wacc: number) => void;
  currentWacc?: number | null;
  initialWaccFromDB?: number | null;
}

interface CashFlowResult {
  period: string;
  freeCashFlowToTheFirm: number;
  discountingPeriod: number;
  discountingFactor: number;
  presentValueOfCashflow: number;
}

interface ValuationData {
  projectName: string;
  waccUsed: number;
  results: CashFlowResult[];
  enterpriseValue: number;
  actualEnterpriseValue: number;
}

const UserAssumption: React.FC<UserAssumptionProps> = ({
  isOpen,
  onClose,
  projectName,
  onValuationUpdate,
  currentWacc,
  initialWaccFromDB,
}) => {
  const [wacc, setWacc] = useState<number>(0);
  const [initialWacc, setInitialWacc] = useState<number>(0);
  const [valuationData, setValuationData] = useState<ValuationData | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [isRecalculating, setIsRecalculating] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  
  const isInitializedRef = useRef(false);
  const projectNameRef = useRef(projectName);

  useEffect(() => {
    if (!isOpen) {
      isInitializedRef.current = false;
      return;
    }

    if (projectName === "Project") return;

    // Only initialize once when modal opens or project changes
    if (!isInitializedRef.current || projectNameRef.current !== projectName) {
      projectNameRef.current = projectName;
      isInitializedRef.current = true;

      const w = currentWacc ?? initialWaccFromDB ?? 0;

      if (initialWaccFromDB) setInitialWacc(initialWaccFromDB);
      setWacc(w);

      fetchValuationData(w, true);
    }
  }, [isOpen, projectName]);

  const fetchValuationData = async (waccPercentage: number, isInitialLoad: boolean = false) => {
    if (projectName === "Project") return;

    // Only set loading on initial load, use isRecalculating for updates
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setIsRecalculating(true);
    }
    
    setError("");

    try {
      const waccDecimal = waccPercentage / 100;
      const response = await service.calculateValuation(
        projectName,
        waccDecimal
      );
      console.log("response of valuation invits:", response);

      if (response.success && response.data) {
        setValuationData(response.data);
      }
    } catch (err) {
      console.error("Error fetching valuation data:", err);
      setError("Failed to load valuation data");
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setIsRecalculating(false);
      }
    }
  };

  const handleWaccChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);

    if (initialWacc === 0) return;

    const minWacc = initialWacc - 2;
    const maxWacc = initialWacc + 2;

    if (value >= minWacc && value <= maxWacc) {
      setWacc(value);
      fetchValuationData(value, false);
    }
  };

  const handleWaccIncrement = () => {
    if (initialWacc === 0) return;

    const newValue = Math.min(wacc + 0.01, initialWacc + 2);
    const roundedValue = parseFloat(newValue.toFixed(2));
    setWacc(roundedValue);
    fetchValuationData(roundedValue, false);
  };

  const handleWaccDecrement = () => {
    if (initialWacc === 0) return;

    const newValue = Math.max(wacc - 0.01, initialWacc - 2);
    const roundedValue = parseFloat(newValue.toFixed(2));
    setWacc(roundedValue);
    fetchValuationData(roundedValue, false);
  };

  if (!isOpen) return null;

  const displayResults = valuationData?.results || [];
  const showContent = !loading && displayResults.length > 0;

  return (
    <div className="user-assumption-overlay" onClick={onClose}>
      <div
        className="user-assumption-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="user-assumption-modal__close" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <h2 className="user-assumption-modal__title">
          Based on Users Assumptions
        </h2>

        <div className="user-assumption-modal__content">
          {loading && (
            <div className="user-assumption-loading">
              <p>Loading valuation data...</p>
            </div>
          )}

          {error && (
            <div className="user-assumption-error">
              <p>{error}</p>
            </div>
          )}

          {showContent && (
            <>
              <div
                className="user-assumption-table-wrapper"
                style={{ position: "relative" }}
              >
                {isRecalculating && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: "rgba(255, 255, 255, 0.8)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 10,
                      borderRadius: "8px",
                    }}
                  >
                    <p style={{ fontSize: "14px", color: "#666", fontWeight: 500 }}>
                      Recalculating...
                    </p>
                  </div>
                )}
                <table className="user-assumption-table">
                  <thead>
                    <tr>
                      <th className="user-assumption-table__sticky-col">
                        Particulars
                      </th>
                      {displayResults.map((result, idx) => (
                        <th key={idx}>{result.period}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="user-assumption-table__label user-assumption-table__sticky-col">
                        Free cash flows to the firm
                      </td>
                      {displayResults.map((result, idx) => (
                        <td key={idx}>
                          {result.freeCashFlowToTheFirm.toFixed(2)}
                        </td>
                      ))}
                    </tr>
                    <tr className="user-assumption-table__divider">
                      <td colSpan={displayResults.length + 1}></td>
                    </tr>
                    <tr>
                      <td className="user-assumption-table__label user-assumption-table__sticky-col">
                        Present value of cash flows
                      </td>
                      {displayResults.map((result, idx) => (
                        <td key={idx}>
                          {result.presentValueOfCashflow.toFixed(1)}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="user-assumption-input-section">
                <div className="user-assumption-input-group">
                  <div className="user-assumption-input-row">
                    <label className="user-assumption-input-label">WACC</label>
                    <div className="user-assumption-input-wrapper">
                      <div className="user-assumption-input-container">
                        <input
                          type="number"
                          className="user-assumption-input"
                          value={wacc.toFixed(2)}
                          onChange={handleWaccChange}
                          min={initialWacc > 0 ? initialWacc - 2 : 0}
                          max={initialWacc > 0 ? initialWacc + 2 : 100}
                          step="0.01"
                          disabled={isRecalculating || initialWacc === 0}
                        />
                        <span className="user-assumption-input-percent">%</span>
                        <div className="user-assumption-input-arrows">
                          <button
                            className="user-assumption-input-arrow-button"
                            onClick={handleWaccIncrement}
                            disabled={
                              wacc >= initialWacc + 2 ||
                              isRecalculating ||
                              initialWacc === 0
                            }
                            type="button"
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 12 12"
                              fill="none"
                            >
                              <path d="M6 3L9 6L3 6L6 3Z" fill="currentColor" />
                            </svg>
                          </button>
                          <button
                            className="user-assumption-input-arrow-button"
                            onClick={handleWaccDecrement}
                            disabled={
                              wacc <= initialWacc - 2 ||
                              isRecalculating ||
                              initialWacc === 0
                            }
                            type="button"
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 12 12"
                              fill="none"
                            >
                              <path d="M6 9L3 6L9 6L6 9Z" fill="currentColor" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  {initialWacc > 0 && (
                    <p className="user-assumption-range-text">
                      Adjustable range: {(initialWacc - 2).toFixed(2)}% to{" "}
                      {(initialWacc + 2).toFixed(2)}%
                    </p>
                  )}
                </div>
                <div className="user-assumption-input-row-with-button">
                  <div className="user-assumption-input-group user-assumption-input-group--no-margin">
                    <div className="user-assumption-input-row">
                      <label className="user-assumption-input-label">
                        Actual Enterprise Value
                      </label>
                      <div className="user-assumption-input-wrapper-single">
                        <input
                          type="text"
                          className="user-assumption-input user-assumption-input--readonly"
                          value={
                            valuationData?.actualEnterpriseValue?.toFixed(2) ||
                            "0"
                          }
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                  <button
                    className="user-assumption-calculate-button"
                    onClick={() => {
                      if (valuationData && onValuationUpdate) {
                        onValuationUpdate(
                          valuationData.actualEnterpriseValue,
                          wacc
                        );
                      }
                      onClose();
                    }}
                    disabled={isRecalculating}
                  >
                    Calculate
                  </button>
                </div>
              </div>
            </>
          )}

          {!loading && !error && displayResults.length === 0 && (
            <div className="user-assumption-empty">
              <p>No valuation data available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserAssumption;